import React, { forwardRef, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";

export interface SearchBarProps {
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  disabled?: boolean;
}

const sizeClasses = {
  xs: "px-10 py-1 text-xs",
  sm: "px-10 py-2 text-sm",
  md: "px-10 py-4 text-base",
  lg: "px-10 py-6 text-base",
  xl: "px-10 py-8 text-lg",
};

export const SearchBar = forwardRef<HTMLInputElement, SearchBarProps>(
  (
    {
      placeholder = "Search",
      value = "",
      onChange,
      className,
      size = "md",
      disabled = false,
    },
    ref
  ) => {
    const [valueLocal, setValueLocal] = useState<string>(value);

    useEffect(() => {
      onChange?.(valueLocal);
    }, [valueLocal]);

    const handleClear = () => {
      setValueLocal("");
    };

    return (
      <div className={twMerge("relative w-full", className)}>
        <div className="absolute h-full ml-3 flex items-center justify-center">
          <MagnifyingGlassIcon className="size-5 text-chariot-text-secondary" />
        </div>
        <input
          type="text"
          ref={ref}
          value={valueLocal}
          onChange={(e) => {
            setValueLocal(e.target.value);
          }}
          placeholder={placeholder}
          disabled={disabled}
          className={twMerge(
            "w-full text-base bg-chariot-transparent text-chariot-text-primary rounded-md pl-10 pr-10 p-3",
            "border-0",
            "placeholder:text-chariot-text-secondary",
            disabled && "cursor-not-allowed opacity-75",
            sizeClasses[size]
          )}
          data-testid="search-bar"
        />
        {value && (
          <button
            onClick={handleClear}
            className="absolute top-0 right-3 h-full flex items-center justify-center"
            aria-label="Clear search"
          >
            <XMarkIcon className="size-5 text-chariot-text-primary hover:text-chariot-text-primary transition-colors" />
          </button>
        )}
      </div>
    );
  }
);
